import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClockIcon } from '@heroicons/react/24/outline';
import { laufwerkAenderungen, type LaufwerkAenderung } from '../../api/client';
import { scanDauer } from '../../utils/laufwerk';

/**
 * Karte „Letzte Änderungen": was auf dem Laufwerk wann geändert wurde, wann der Connector es erkannt hat und ab
 * wann es durchsuchbar war. Nur laufende Änderungen (nicht die Erstaufnahme), 30 Tage. Aktualisiert sich alle 5 s.
 */

const ART: Record<string, { label: string; badge: string }> = {
  neu: { label: 'Neu', badge: 'badge-ok' },
  geaendert: { label: 'Geändert', badge: 'badge-brand' },
  verschoben: { label: 'Verschoben', badge: 'badge-neutral' },
  geloescht: { label: 'Gelöscht', badge: 'badge-err' },
};

function zeit(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const heute = new Date().toDateString() === d.toDateString();
  const uhr = d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return heute ? uhr : `${d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })} ${uhr}`;
}

function ergebnis(a: LaufwerkAenderung): { text: string; klasse: string } {
  const nach = a.sekunden !== null ? ` nach ${scanDauer(a.sekunden)}` : '';
  switch (a.ergebnis) {
    case null: return { text: 'wird verarbeitet …', klasse: 'text-amber-700' };
    case 'indexiert': return { text: `durchsuchbar${nach}`, klasse: 'text-emerald-700' };
    case 'unveraendert': return { text: `Inhalt unverändert${nach}`, klasse: 'text-ps-dark-500' };
    case 'nicht_unterstuetzt': return { text: 'nur Name durchsuchbar (Dateityp ohne Inhalt)', klasse: 'text-ps-dark-500' };
    case 'zu_gross': return { text: 'nur Name durchsuchbar (zu gross)', klasse: 'text-ps-dark-500' };
    case 'fehler': return { text: `nicht lesbar${nach}`, klasse: 'text-rose-700' };
    case 'entfernt': return { text: 'aus dem Index entfernt', klasse: 'text-ps-dark-600' };
    case 'nachgefuehrt': return { text: 'Pfad nachgeführt, nicht neu gelesen', klasse: 'text-ps-dark-600' };
    case 'ueberholt': return { text: 'durch neuere Änderung ersetzt', klasse: 'text-ps-dark-400' };
    case 'geloescht': return { text: 'vor der Verarbeitung gelöscht', klasse: 'text-ps-dark-400' };
    default: return { text: a.ergebnis, klasse: 'text-ps-dark-500' };
  }
}

const name = (p: string) => p.slice(p.lastIndexOf('/') + 1);
const ordner = (p: string) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '(oberste Ebene)');

export default function AenderungenKarte() {
  const [liste, setListe] = useState<LaufwerkAenderung[] | null>(null);
  const [mehr, setMehr] = useState(true);

  const laden = useCallback(async () => {
    try {
      const neu = await laufwerkAenderungen();
      // die ersten 50 frisch, bereits nachgeladene ältere behalten
      setListe((alt) => {
        if (!alt || alt.length <= neu.length) return neu;
        const da = new Set(neu.map((a) => a.id));
        return [...neu, ...alt.filter((a) => !da.has(a.id) && a.id < neu[neu.length - 1].id)];
      });
    } catch { /* Verlauf ist Zugabe — Fehler zeigt die Statuskarte */ }
  }, []);

  useEffect(() => {
    void laden();
    const t = setInterval(() => { if (!document.hidden) void laden(); }, 5_000);
    return () => clearInterval(t);
  }, [laden]);

  const aelter = async () => {
    if (!liste?.length) return;
    const weitere = await laufwerkAenderungen(liste[liste.length - 1].id);
    setMehr(weitere.length >= 50);
    setListe([...liste, ...weitere]);
  };

  return (
    <div className="card space-y-3">
      <div>
        <h2 className="page-h1 flex items-center gap-2"><ClockIcon className="h-5 w-5 text-ps-dark-400" /> Letzte Änderungen</h2>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ps-dark-400">
          Was auf dem Laufwerk neu dazugekommen, geändert, verschoben oder gelöscht wurde — mit dem Zeitpunkt der Änderung,
          wann sie bemerkt wurde und ab wann der Inhalt durchsuchbar war. Aktualisiert sich alle 5 Sekunden; die Erstaufnahme erscheint hier nicht, Einträge bleiben 30 Tage.
        </p>
      </div>

      {liste === null ? (
        <div className="space-y-2"><div className="shimmer h-3 w-full rounded" /><div className="shimmer h-3 w-2/3 rounded" /></div>
      ) : liste.length === 0 ? (
        <div className="rounded-lg border border-dashed border-ps-dark-200 px-4 py-6 text-center text-sm text-ps-dark-500">
          Seit dem Start wurde auf dem Laufwerk nichts geändert.
        </div>
      ) : (
        <>
          <div className="hidden grid-cols-[6.5rem_6rem_minmax(0,1fr)_7rem_minmax(0,14rem)] gap-3 border-b border-gray-200 px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ps-dark-400 md:grid">
            <span>Bemerkt</span><span>Art</span><span>Datei</span><span>Geändert</span><span>Index</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {liste.map((a) => {
              const art = ART[a.art] ?? { label: a.art, badge: 'badge-neutral' };
              const e = ergebnis(a);
              return (
                <li key={a.id} className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 px-2 py-2 text-sm md:grid-cols-[6.5rem_6rem_minmax(0,1fr)_7rem_minmax(0,14rem)] md:items-start">
                  <span className="numerical text-[12px] text-ps-dark-500 md:pt-0.5">{zeit(a.erkannt)}</span>
                  <span className="md:pt-px"><span className={`badge ${art.badge}`}>{art.label}</span></span>
                  <div className="col-span-2 min-w-0 md:col-span-1">
                    {a.datei_id ? (
                      <Link to={`/datei/${a.datei_id}`} className="break-words font-medium text-ps-dark-900 hover:text-primary-700 hover:underline">{name(a.pfad)}</Link>
                    ) : <span className="break-words font-medium text-ps-dark-700">{name(a.pfad)}</span>}
                    <div className="break-all text-[11px] text-ps-dark-400">
                      {a.art === 'verschoben' && a.alt_pfad ? <>von {a.alt_pfad}<br />nach {ordner(a.pfad)}</> : ordner(a.pfad)}
                    </div>
                  </div>
                  <span className="numerical text-[12px] text-ps-dark-500 md:pt-0.5"><span className="md:hidden">geändert </span>{zeit(a.mtime)}</span>
                  <span className={`text-[12px] md:pt-0.5 ${e.klasse}`}>{e.text}</span>
                </li>
              );
            })}
          </ul>
          {mehr && liste.length >= 50 && (
            <button onClick={aelter} className="w-full rounded-md py-2 text-[12px] text-primary-700 hover:bg-gray-50">Ältere Änderungen laden</button>
          )}
        </>
      )}
    </div>
  );
}
